import { KeyboardEvent, useCallback } from 'react';

import { NavigationMode, useFocusContext } from './JabraFocusContext';

export const useArrowKeyHandler = () => {
  const { moveFocus, orientation } = useFocusContext();

  return useCallback(
    (event: KeyboardEvent) => {
      const delta = getMoveDelta(event.key, orientation);
      if (delta === undefined) {
        return;
      }

      if (delta !== 0 && moveFocus(delta)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [orientation, moveFocus]
  );
};

export const getMoveDelta = (key: string, orientation: NavigationMode): number | undefined => {
  switch (key) {
    case 'ArrowUp':
      if (orientation === 'vertical' || orientation === 'both') {
        return -1;
      }
      return 0;
    case 'ArrowLeft':
      if (orientation === 'horizontal' || orientation === 'both') {
        return -1;
      }
      return 0;

    case 'ArrowDown':
      if (orientation === 'vertical' || orientation === 'both') {
        return 1;
      }
      return 0;
    case 'ArrowRight':
      if (orientation === 'horizontal' || orientation === 'both') {
        return 1;
      }
      return 0;
    default:
      return;
  }
};

/**
 * Determines whether an element can actually receive focus via the Tab key.
 * A matching selector (button, [href], input, ...) is not enough: disabled,
 * aria-hidden, tabindex="-1" or visually hidden elements are skipped by the
 * browser, so they must be excluded when locating the first/last tab stop.
 */
export const isTabbable = (element: HTMLElement): boolean => {
  if (element.hasAttribute('disabled') || element.getAttribute('aria-hidden') === 'true') {
    return false;
  }

  const tabIndex = element.getAttribute('tabindex');
  if (tabIndex !== null && Number(tabIndex) < 0) {
    return false;
  }

  const { visibility, display } = window.getComputedStyle(element);
  if (visibility === 'hidden' || display === 'none') {
    return false;
  }

  return element.getClientRects().length > 0;
};
